import Phaser from "phaser";
import { gameRenderScale } from "./renderScale";
import { TRADE_QUARTER, TRADE_QUARTER_ART } from "../config/tradeQuarter";
import { fitTradeArt, tradeFramesMatch, type ArtFrame } from "./tradeArtLayout";
import { advanceTradeMotion, tradeCrystalLift } from "./tradeQuarterMotion";
import { TradeWaterfrontEffects } from "./TradeWaterfrontEffects";

export type TradeQuarterRuntime = { destroy(): void; setPaused(paused: boolean): void; focus(target: string | null): void };

/** The canvas and accessible map buttons share one fitted artboard: hit targets never drift. */
export function createTradeQuarterGame(parent: HTMLElement, reducedMotion: boolean): TradeQuarterRuntime {
  reducedMotion ||= window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ratio = gameRenderScale(window.devicePixelRatio || 1, document.documentElement.dataset.platform);
  let paused = false;
  const markReady = (): void => {
    // Container-query positions can settle after our layout read, even without
    // another size change. Verify the DRAWN frame before replacing the fallback.
    if (!scene.layoutMatchesDom()) { scene.layout(); return; }
    parent.dataset.tradeSceneReady = "true";
  };
  const mapRoot = parent.closest(".trade-quarter-map");
  // The fixed DOM artboard is the coordinate system for both rendering and hit
  // targets. Read it only on layout/resize, never in the animation update loop.
  const frameOf = (element: Element | null): ArtFrame | null => {
    if (!element) return null;
    const surface = parent.getBoundingClientRect(), box = element.getBoundingClientRect();
    return { x: (box.x - surface.x) * ratio, y: (box.y - surface.y) * ratio, width: box.width * ratio, height: box.height * ratio };
  };
  class TradeQuarterScene extends Phaser.Scene {
    map?: Phaser.GameObjects.Image;
    ocean?: Phaser.GameObjects.Image;
    effects?: TradeWaterfrontEffects;
    crystal?: Phaser.GameObjects.Image;
    glint?: Phaser.GameObjects.Graphics;
    marker?: Phaser.GameObjects.Graphics;
    crystalFrame?: ArtFrame;
    motionTime = 0;
    focusedTarget: string | null = null;
    preload() {
      this.load.image("trade-map", TRADE_QUARTER_ART.map);
      this.load.image("trade-ocean", TRADE_QUARTER_ART.ocean);
      this.load.image("trade-skiff", TRADE_QUARTER_ART.skiff);
      this.load.image("trade-crystal", TRADE_QUARTER_ART.crystal);
    }
    create() {
      // Failed textures leave the identical DOM fallback, never a missing-texture square.
      if (!["trade-crystal", "trade-map", "trade-ocean"].every(key => this.textures.exists(key))) return;
      this.ocean = this.add.image(0, 0, "trade-ocean").setOrigin(0).setDepth(0);
      this.map = this.add.image(0, 0, "trade-map").setOrigin(0).setDepth(3);
      this.effects = new TradeWaterfrontEffects(this, ratio, reducedMotion);
      this.crystal = this.add.image(0, 0, "trade-crystal").setOrigin(0).setDepth(5);
      this.glint = this.add.graphics().lineStyle(1.4 * ratio, 0xffffff, .9)
        .lineBetween(-4 * ratio, 0, 4 * ratio, 0).lineBetween(0, -4 * ratio, 0, 4 * ratio).setAlpha(.35).setDepth(6);
      this.marker = this.add.graphics().setDepth(6);
      this.layout();
      parent.dataset.tradeMotion = reducedMotion ? "reduced" : "active";
      if (paused) this.scene.pause();
    }
    layout() {
      if (!this.map || !this.crystal) return;
      // DOM artwork responds to viewport/safe-area changes immediately. Do not
      // expose a stretched or stale canvas until the matching frame is drawn.
      parent.dataset.tradeSceneReady = "false";
      const background = frameOf(parent.querySelector(".trade-quarter-map-fallback"));
      const crystal = frameOf(mapRoot?.querySelector(".trade-quarter-crystal") ?? null);
      if (this.ocean) {
        const scale = Math.max(this.scale.width / TRADE_QUARTER.width, this.scale.height / TRADE_QUARTER.height);
        this.ocean.setScale(scale).setPosition((this.scale.width - TRADE_QUARTER.width * scale) / 2, (this.scale.height - TRADE_QUARTER.height * scale) / 2);
      }
      if (background) {
        this.map?.setPosition(background.x, background.y).setScale(background.width / TRADE_QUARTER.width);
        const seaTop = this.ocean ? this.ocean.y + this.ocean.displayHeight * .18 : 0;
        this.effects?.layout(background, this.scale.width, background.y + background.height * TRADE_QUARTER.crystal.y, seaTop);
      }
      if (this.crystal && crystal) {
        const fit = fitTradeArt(this.crystal, crystal, false);
        this.crystalFrame = fit;
        this.crystal.setPosition(fit.x, fit.y).setDisplaySize(fit.width, fit.height);
        this.positionMotion();
      }
      this.focus(this.focusedTarget);
      this.game.events.off(Phaser.Core.Events.POST_RENDER, markReady);
      this.game.events.once(Phaser.Core.Events.POST_RENDER, markReady);
    }
    layoutMatchesDom(): boolean {
      if (!this.map || !this.crystal) return false;
      const background = frameOf(parent.querySelector(".trade-quarter-map-fallback"));
      const crystal = frameOf(mapRoot?.querySelector(".trade-quarter-crystal") ?? null);
      return tradeFramesMatch(background, { x: this.map.x, y: this.map.y, width: this.map.displayWidth, height: this.map.displayHeight }) &&
        tradeFramesMatch(crystal ? fitTradeArt(this.crystal, crystal, false) : null, this.crystalFrame);
    }
    positionMotion() {
      if (!this.crystal || !this.crystalFrame) return;
      const motion = TRADE_QUARTER.motion;
      const lift = tradeCrystalLift(this.motionTime, this.crystalFrame.height, reducedMotion);
      this.crystal.y = this.crystalFrame.y - lift;
      this.glint?.setPosition(this.crystal.x + this.crystal.displayWidth * .53, this.crystal.y + this.crystal.displayHeight * .17)
        .setAlpha(reducedMotion ? .35 : .28 + .3 * (.5 + .5 * Math.sin(this.motionTime * Math.PI * 2 / motion.glintPeriodMs)));
      this.effects?.update(this.motionTime);
    }
    override update(_time: number, delta: number) {
      if (reducedMotion) return;
      // Keep phase across layout and pause/resume. A suspended tab must not
      // advance through minutes of animation on its first visible frame.
      this.motionTime = advanceTradeMotion(this.motionTime, delta);
      this.positionMotion();
    }
    focus(target: string | null) {
      this.focusedTarget = target;
      if (!this.marker) return;
      this.tweens.killTweensOf(this.marker);
      this.marker.clear();
      const b = TRADE_QUARTER.buildings.find(item => item.id === target);
      if (!b) return;
      const plaque = frameOf(mapRoot?.querySelector(`[data-tab="${b.id}"] .trade-quarter-plaque`) ?? null);
      if (!plaque) return;
      const x = plaque.x + plaque.width / 2, y = plaque.y - 5 * ratio, r = 4 * ratio;
      // A short diamond-shaped locator above the name, not a glowing oval over the building.
      this.marker.fillStyle(0xffedb1, 1).fillPoints([{ x, y: y - r }, { x: x + r, y }, { x, y: y + r }, { x: x - r, y }], true);
      this.marker.setAlpha(reducedMotion ? 1 : 0);
      if (!reducedMotion) this.tweens.add({ targets: this.marker, alpha: 1, duration: 180, ease: "Sine.Out" });
    }
  }
  const scene = new TradeQuarterScene({ key: "TradeQuarter" });
  const waterTap = (event: Event) => {
    if (!(event instanceof MouseEvent) || (event.target instanceof Element && event.target.closest("button")) || !scene.effects || !scene.map) return;
    const box = parent.getBoundingClientRect(), x = (event.clientX - box.x) * ratio, y = (event.clientY - box.y) * ratio;
    if (y < scene.scale.height * .22) return;
    const f = scene.effects.frame, ax = (x - f.x) / f.width * TRADE_QUARTER.width, ay = (y - f.y) / f.height * TRADE_QUARTER.height;
    const alpha = ax < 0 || ay < 0 || ax >= TRADE_QUARTER.width || ay >= TRADE_QUARTER.height ? 0
      : scene.textures.getPixelAlpha(Math.floor(ax), Math.floor(ay), "trade-map") ?? 0;
    if (alpha < 20) scene.effects.touchWater(x, y);
  };
  mapRoot?.addEventListener("click", waterTap);
  const canvas = document.createElement("canvas"); canvas.setAttribute("aria-hidden", "true");
  const game = new Phaser.Game({ type: Phaser.CANVAS, canvas, parent, transparent: true, antialias: true,
    width: Math.max(1, Math.round(parent.clientWidth * ratio)), height: Math.max(1, Math.round(parent.clientHeight * ratio)),
    banner: false, audio: { noAudio: true }, fps: { target: reducedMotion ? 15 : 60, limit: reducedMotion ? 15 : 60 },
    scale: { mode: Phaser.Scale.NONE }, scene: [scene] });
  let frame = 0, settledFrame = 0;
  const settleLayout = () => {
    parent.dataset.tradeSceneReady = "false";
    game.events.off(Phaser.Core.Events.POST_RENDER, markReady);
    // Safe-area changes can move the artwork without resizing it. In WebViews
    // container-query positions settle after the resize frame; a ResizeObserver
    // alone cannot catch that second, position-only change.
    cancelAnimationFrame(frame); cancelAnimationFrame(settledFrame);
    frame = requestAnimationFrame(() => {
      frame = 0;
      settledFrame = requestAnimationFrame(() => {
        settledFrame = 0;
        if (scene.sys.isActive()) scene.layout();
      });
    });
  };
  const resize = new ResizeObserver(() => {
    const width = Math.max(1, Math.round(parent.clientWidth * ratio)), height = Math.max(1, Math.round(parent.clientHeight * ratio));
    if (game.scale.width !== width || game.scale.height !== height) game.scale.resize(width, height);
    settleLayout();
  });
  resize.observe(parent);
  // Container-query units on descendants can settle after the parent's resize
  // delivery. Observe the actual artwork too, then read coordinates next frame.
  const background = parent.querySelector(".trade-quarter-map-fallback");
  const crystal = mapRoot?.querySelector(".trade-quarter-crystal");
  if (background) resize.observe(background);
  if (crystal) resize.observe(crystal);
  const safeArea = new MutationObserver(settleLayout);
  safeArea.observe(document.documentElement, { attributes: true, attributeFilter: ["style", "class"] });
  return {
    focus(target) { if (scene.sys.isActive()) scene.focus(target); },
    setPaused(value) { paused = value; if (value && scene.sys.isActive()) scene.scene.pause(); else if (!value && scene.sys.isPaused()) { scene.scene.resume(); settleLayout(); } },
    destroy() { mapRoot?.removeEventListener("click", waterTap); resize.disconnect(); safeArea.disconnect(); cancelAnimationFrame(frame); cancelAnimationFrame(settledFrame); game.events.off(Phaser.Core.Events.POST_RENDER, markReady); game.destroy(true); canvas.remove(); delete parent.dataset.tradeSceneReady; delete parent.dataset.tradeMotion; },
  };
}
